import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { useFinance } from '../context/FinanceContext';
import { useTheme } from '../context/ThemeContext';
import {
  Settings as SettingsIcon, Globe, Moon, Bell, Database,
  User, LogOut, Save, Shield, Palette, Layout, CreditCard, Type, BarChart3, Table2, Lock
} from 'lucide-react';

const settingKeys = [
  { key: 'currency', label: 'Moeda Principal', type: 'select', icon: Globe, options: ['AOA', 'USD', 'EUR', 'GBP', 'BRL'] },
  { key: 'language', label: 'Idioma', type: 'select', icon: Globe, options: ['pt', 'en'] },
  { key: 'date_format', label: 'Formato de Data', type: 'select', icon: SettingsIcon, options: ['dd/MM/yyyy', 'MM/dd/yyyy', 'yyyy-MM-dd'] },
  { key: 'theme', label: 'Tema', type: 'select', icon: Moon, options: ['dark', 'light', 'auto'] },
  { key: 'notifications_enabled', label: 'Notificações', type: 'toggle', icon: Bell },
  { key: 'budget_alerts', label: 'Alertas de Orçamento', type: 'toggle', icon: Shield },
];

export default function SettingsPage() {
  const { user, logout, fetchSettings: ctxFetchSettings, settings: ctxSettings } = useFinance();
  const { theme, setTheme, sidebarMode, setSidebarMode, mobileLayout, setMobileLayout, cardShape, setCardShape, font, setFont, fonts, chartStyle, setChartStyle, chartStyles, tableStyle, setTableStyle, tableStyles, uiArchitecture, setUiArchitecture } = useTheme();

  // Filtrar tema das chaves do backend (será gerido via Context)
  const filteredSettingKeys = settingKeys.filter(s => s.key !== 'theme');

  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [exchangeRates, setExchangeRates] = useState([]);
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    password: '',
    password_confirmation: '',
  });
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState('');
  const [passwordError, setPasswordError] = useState('');

  useEffect(() => {
    Promise.all([
      api.get('/settings'),
      api.get('/exchange-rates'),
    ]).then(([sRes, eRes]) => {
      const obj = {};
      (Array.isArray(sRes.data) ? sRes.data : []).forEach(s => { obj[s.key] = s.value; });
      setSettings(obj);
      setExchangeRates(eRes.data);
      setLoading(false);
    });
  }, []);

  const handleSave = async () => {
    setSaving(true);
    await api.put('/settings', { settings });
    ctxFetchSettings();
    setSaving(false);
  };

  const handleRateUpdate = async (id, rate) => {
    await api.put(`/exchange-rates/${id}`, { rate: parseFloat(rate) });
  };

  const handlePasswordChange = async (event) => {
    event.preventDefault();
    setPasswordSaving(true);
    setPasswordMessage('');
    setPasswordError('');

    try {
      await api.post('/password', passwordForm);
      setPasswordForm({ current_password: '', password: '', password_confirmation: '' });
      setPasswordMessage('Password alterada. As outras sessões foram terminadas.');
    } catch (error) {
      const errors = error.response?.data?.errors;
      setPasswordError(
        errors?.current_password?.[0]
        || errors?.password?.[0]
        || error.response?.data?.message
        || 'Não foi possível alterar a password.'
      );
    } finally {
      setPasswordSaving(false);
    }
  };

  if (loading) {
    return <div className="skeleton" style={{ height: 400, borderRadius: 16 }} />;
  }

  return (
    <div style={{ maxWidth: 640, margin: '0 auto' }}>
      <div className="page-header">
        <div>
          <h1 className="page-title">Configurações</h1>
          <p className="page-subtitle">Personalize a sua experiência</p>
        </div>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
          <Save size={16} /> {saving ? 'A guardar...' : 'Guardar'}
        </button>
      </div>

      {/* Perfil */}
      <div className={`card mb-md variant-${cardShape}`}>
        <div className="section-header">
          <h3 className="section-title"><User size={18} style={{ verticalAlign: 'middle', marginRight: 8 }} />Perfil</h3>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{
            width: 56, height: 56, borderRadius: 16, background: 'var(--gradient-blue)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 22, fontWeight: 700, color: 'white', flexShrink: 0,
          }}>
            {user?.name?.charAt(0)?.toUpperCase() || 'U'}
          </div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700 }}>{user?.name || 'Utilizador'}</div>
            <div style={{ fontSize: 13, color: 'var(--text-tertiary)' }}>{user?.email}</div>
          </div>
        </div>
      </div>

      <div className={`card mb-md variant-${cardShape}`}>
        <div className="section-header">
          <h3 className="section-title"><Lock size={18} style={{ verticalAlign: 'middle', marginRight: 8 }} />Segurança</h3>
        </div>
        <form onSubmit={handlePasswordChange} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {passwordMessage && (
            <div style={{ padding: 12, borderRadius: 8, background: 'var(--accent-green-dim)', color: 'var(--accent-green)', fontSize: 13 }}>
              {passwordMessage}
            </div>
          )}
          {passwordError && (
            <div style={{ padding: 12, borderRadius: 8, background: 'var(--accent-red-dim)', color: 'var(--accent-red)', fontSize: 13 }}>
              {passwordError}
            </div>
          )}
          <div className="form-group">
            <label className="form-label">Password actual</label>
            <input
              className="form-input"
              type="password"
              value={passwordForm.current_password}
              onChange={event => setPasswordForm({ ...passwordForm, current_password: event.target.value })}
              required
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Nova password</label>
              <input
                className="form-input"
                type="password"
                minLength={8}
                value={passwordForm.password}
                onChange={event => setPasswordForm({ ...passwordForm, password: event.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Confirmar password</label>
              <input
                className="form-input"
                type="password"
                minLength={8}
                value={passwordForm.password_confirmation}
                onChange={event => setPasswordForm({ ...passwordForm, password_confirmation: event.target.value })}
                required
              />
            </div>
          </div>
          <button className="btn btn-secondary" type="submit" disabled={passwordSaving} style={{ alignSelf: 'flex-start' }}>
            <Shield size={16} /> {passwordSaving ? 'A alterar...' : 'Alterar password'}
          </button>
        </form>
      </div>

      {/* Personalização */}
      <div className={`card mb-md variant-${cardShape}`}>
        <div className="section-header">
          <h3 className="section-title"><Palette size={18} style={{ verticalAlign: 'middle', marginRight: 8 }} />Personalização</h3>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Tema */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Moon size={18} color="var(--text-tertiary)" />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 500 }}>Tema do Sistema</div>
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>Escolha entre 10 paletas premium</div>
            </div>
            <select
              className="form-input form-select" style={{ width: 160 }}
              value={theme}
              onChange={e => setTheme(e.target.value)}
            >
              <option value="default">Original Dark</option>
              <option value="light">Light Mode</option>
              <option value="navy">Deep Navy</option>
              <option value="dracula">Dracula</option>
              <option value="forest">Forest</option>
              <option value="teal">Cyber Teal</option>
              <option value="sunset">Sunset Gold</option>
              <option value="midnight">Midnight Black</option>
              <option value="nord">Nord Arctic</option>
              <option value="rosewater">Rosewater</option>
            </select>
          </div>

          {/* Sidebar Layout */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Layout size={18} color="var(--text-tertiary)" />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 500 }}>Layout da Sidebar</div>
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>Estrutura visual dos ícones e menus</div>
            </div>
            <select
              className="form-input form-select" style={{ width: 160 }}
              value={sidebarMode}
              onChange={e => setSidebarMode(e.target.value)}
            >
              <option value="modern">Modern (Default)</option>
              <option value="compact">Compact (Icons Only)</option>
              <option value="classic">Classic (Linear)</option>
              <option value="floating">Floating (Destacado)</option>
              <option value="glass">Glass (Translúcido)</option>
            </select>
          </div>

          {/* Mobile Layout */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Layout size={18} color="var(--text-tertiary)" />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 500 }}>Layout Mobile</div>
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>Navegação em telas pequenas</div>
            </div>
            <select
              className="form-input form-select" style={{ width: 160 }}
              value={mobileLayout}
              onChange={e => setMobileLayout(e.target.value)}
            >
              <option value="bottom-nav">Bottom Nav (Padrão)</option>
              <option value="sidebar">Sidebar (Drawer)</option>
            </select>
          </div>

          {/* Card Shape */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <CreditCard size={18} color="var(--text-tertiary)" />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 500 }}>Geometria dos Cards</div>
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>Formato físico dos componentes</div>
            </div>
            <select
              className="form-input form-select" style={{ width: 160 }}
              value={cardShape}
              onChange={e => setCardShape(e.target.value)}
            >
              <option value="sleek">Sleek (Default)</option>
              <option value="round">Rounded (Amigável)</option>
              <option value="sharp">Sharp (Profissional)</option>
              <option value="minimal">Minimalist</option>
            </select>
          </div>

          {/* Arquitetura Visual (Avançada) */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Palette size={18} color="var(--text-tertiary)" />
            <div className="flex-1">
              <div style={{ fontSize: 14, fontWeight: 500 }}>Arquitetura Visual</div>
              <div style={{ fontSize: 11, color: 'var(--accent-purple)' }}>[BETA] Transforma a estrutura da UI</div>
            </div>
            <select
              className="form-input form-select" style={{ width: 160 }}
              value={uiArchitecture}
              onChange={e => setUiArchitecture(e.target.value)}
            >
              <option value="standard">Standard (Padrão)</option>
              <option value="apple_glass">Apple Glass (macOS)</option>
              <option value="neumorphic">Neumorphism (Relevo)</option>
              <option value="clay">Claymorphism (3D Fofo)</option>
              <option value="cyberneon">CyberNeon (Brilhos)</option>
              <option value="neobrutalism">NeoBrutalismo (Pop)</option>
              <option value="brutalism">Retro Brutalism</option>
              <option value="origami">Origami (Papel)</option>
            </select>
          </div>

          {/* Tipografia */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Type size={18} color="var(--text-tertiary)" />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 500 }}>Tipografia</div>
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>Fonte principal da aplicação</div>
            </div>
            <select
              className="form-input form-select" style={{ width: 160 }}
              value={font}
              onChange={e => setFont(e.target.value)}
            >
              {Object.values(fonts).map(f => (
                <option key={f} value={f}>{f}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Preferências */}
      <div className={`card mb-md variant-${cardShape}`}>
        <div className="section-header">
          <h3 className="section-title"><SettingsIcon size={18} style={{ verticalAlign: 'middle', marginRight: 8 }} />Preferências</h3>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {filteredSettingKeys.map(({ key, label, type, icon: Icon, options }) => (
            <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Icon size={18} color="var(--text-tertiary)" />
              <span style={{ flex: 1, fontSize: 14, fontWeight: 500 }}>{label}</span>
              {type === 'select' ? (
                <select className="form-input form-select" style={{ width: 160 }}
                  value={settings[key] || ''} onChange={e => setSettings({ ...settings, [key]: e.target.value })}>
                  {options.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : (
                <label style={{ position: 'relative', display: 'inline-block', width: 44, height: 24 }}>
                  <input type="checkbox" checked={settings[key] === 'true' || settings[key] === true}
                    onChange={e => setSettings({ ...settings, [key]: e.target.checked ? 'true' : 'false' })}
                    style={{ opacity: 0, width: 0, height: 0 }} />
                  <span style={{
                    position: 'absolute', cursor: 'pointer', inset: 0, borderRadius: 12,
                    background: (settings[key] === 'true' || settings[key] === true) ? 'var(--accent-blue)' : 'var(--bg-input)',
                    transition: 'var(--transition-fast)',
                  }}>
                    <span style={{
                      position: 'absolute', content: '', height: 18, width: 18, left: (settings[key] === 'true' || settings[key] === true) ? 23 : 3, bottom: 3,
                      background: 'white', borderRadius: '50%', transition: 'var(--transition-fast)',
                    }} />
                  </span>
                </label>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Visualização de Dados */}
      <div className={`card mb-md variant-${cardShape}`}>
        <div className="section-header">
          <h3 className="section-title"><BarChart3 size={18} style={{ verticalAlign: 'middle', marginRight: 8 }} />Visualização de Dados</h3>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Estilo dos Gráficos */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <BarChart3 size={18} color="var(--text-tertiary)" />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 500 }}>Estilo dos Gráficos</div>
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>Aparência das visualizações financeiras</div>
            </div>
            <select
              className="form-input form-select" style={{ width: 160 }}
              value={chartStyle}
              onChange={e => setChartStyle(e.target.value)}
            >
              <option value="modern">Modern (Barras)</option>
              <option value="elegant">Elegant (Área)</option>
              <option value="minimal">Minimal (Linha)</option>
            </select>
          </div>

          {/* Estilo das Tabelas */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Table2 size={18} color="var(--text-tertiary)" />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 500 }}>Estilo das Tabelas</div>
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>Densidade e separação de listas</div>
            </div>
            <select
              className="form-input form-select" style={{ width: 160 }}
              value={tableStyle}
              onChange={e => setTableStyle(e.target.value)}
            >
              <option value="standard">Standard (Padrão)</option>
              <option value="compact">Compact (Denso)</option>
              <option value="striped">Striped (Zebrado)</option>
              <option value="bordered">Bordered (Bordas)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Taxas de Câmbio */}
      <div className={`card mb-md variant-${cardShape}`}>
        <div className="section-header">
          <h3 className="section-title"><Globe size={18} style={{ verticalAlign: 'middle', marginRight: 8 }} />Taxas de Câmbio</h3>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {exchangeRates.map(rate => (
            <div key={rate.id} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: 14, fontWeight: 600, width: 100 }}>
                1 {rate.from_currency} =
              </span>
              <input className="form-input form-input-money" type="number" step="0.01" style={{ width: 120 }}
                defaultValue={rate.rate}
                onBlur={e => handleRateUpdate(rate.id, e.target.value)} />
              <span style={{ fontSize: 14, color: 'var(--text-tertiary)' }}>AOA</span>
            </div>
          ))}
        </div>
      </div>

      {/* Terminar Sessão */}
      <button className="btn btn-danger" style={{ width: '100%' }} onClick={logout}>
        <LogOut size={18} /> Terminar Sessão
      </button>
    </div>
  );
}
